import React, { useState, useMemo, useCallback } from "react"
import styled from "styled-components"
import FilterDropdown from "../FilterDropdown/FilterDropdown" // our enhanced filter panel
import { CustomSelect } from "@components/base/Dropdown/CustomSelect"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`

const SortControls = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.sm};

  span.text-sort {
    margin-right: 0.4rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const SortSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.secondary};
  border-radius: ${({ theme }) => theme.components.borderRadius.small};
  background-color: ${({ theme }) => theme.white};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.primaryText};
`

const SortToggle = styled.button`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: none;
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.white};
  border-radius: ${({ theme }) => theme.components.borderRadius.small};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.md};
`

export interface FilterAndSortListProps<T> {
  items: T[]
  renderItem: (item: T) => React.ReactNode
  searchFields: (keyof T)[]
  sortOptions: { value: keyof T; label: string }[]
  defaultSort: keyof T
  defaultSortOrder?: "asc" | "desc"
  filterFn?: (item: T, filters: Record<string, any>) => boolean
}

function FilterAndSortList<T>({
  items,
  renderItem,
  searchFields,
  sortOptions,
  defaultSort,
  defaultSortOrder = "asc",
  filterFn,
}: FilterAndSortListProps<T>) {
  // Sort state
  const [sortCriteria, setSortCriteria] = useState<keyof T>(defaultSort)
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(defaultSortOrder)

  // Advanced filters provided by FilterDropdown (reactively)
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({})

  // Memoize a default filter function so its reference remains stable.
  const defaultFilterFn = useCallback(
    (item: T, filters: Record<string, any>) => {
      const global = filters.globalSearch ? filters.globalSearch.toLowerCase() : ""
      const basicMatch = searchFields.some((field) => {
        const val = item[field]
        return typeof val === "string" && val.toLowerCase().includes(global)
      })
      // Advanced filters (example for a few fields)
      const occupants = (item as any).numberOfOccupants
      const occupantMatch =
        (!filters.minOccupants || occupants >= Number(filters.minOccupants)) && (!filters.maxOccupants || occupants <= Number(filters.maxOccupants))
      const animaux = (item as any).animals
      const animauxMatch = !filters.animaux || (Array.isArray(animaux) && animaux.join(", ").toLowerCase().includes(filters.animaux.toLowerCase()))
      const moveDate = new Date((item as any).moveInDate)
      const moveInMatch =
        (!filters.moveInFrom || moveDate >= new Date(filters.moveInFrom)) && (!filters.moveInTo || moveDate <= new Date(filters.moveInTo))
      const submittedDate = new Date((item as any).submittedAt)
      const submittedMatch =
        (!filters.submittedFrom || submittedDate >= new Date(filters.submittedFrom)) &&
        (!filters.submittedTo || submittedDate <= new Date(filters.submittedTo))
      const addressMatch =
        !filters.apartmentAddress ||
        ((item as any).apartmentAddress && (item as any).apartmentAddress.toLowerCase().includes(filters.apartmentAddress.toLowerCase()))
      const statusMatch = filters.status && Array.isArray(filters.status) ? filters.status.includes((item as any).status) : true

      const desiredSizeMatch =
        filters.desiredSize && Array.isArray(filters.desiredSize) && filters.desiredSize.length > 0
          ? Array.isArray((item as any).desiredSize)
            ? filters.desiredSize.some((size: string) => (item as any).desiredSize.includes(size))
            : false
          : true

      return basicMatch && occupantMatch && animauxMatch && moveInMatch && submittedMatch && addressMatch && statusMatch && desiredSizeMatch
    },
    [searchFields],
  )

  const appliedFilterFnFinal = filterFn ? filterFn : defaultFilterFn

  const filteredItems = useMemo(() => {
    return items.filter((item) => appliedFilterFnFinal(item, appliedFilters))
  }, [items, appliedFilters, appliedFilterFnFinal])

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems].sort((a, b) => {
      const aVal = a[sortCriteria]
      const bVal = b[sortCriteria]
      if (typeof aVal === "string" && typeof bVal === "string") {
        return aVal.localeCompare(bVal)
      }
      if (typeof aVal === "number" && typeof bVal === "number") {
        return aVal - bVal
      }
      return 0
    })
    return sortOrder === "asc" ? sorted : sorted.reverse()
  }, [filteredItems, sortCriteria, sortOrder])

  return (
    <Container>
      <SortControls>
        <FilterDropdown onApply={(filters) => setAppliedFilters(filters)} onReset={() => setAppliedFilters({})} isMobile={false} />
        <div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <span className="text-sort"> Trier par:{"  "}</span>
            {/* <SortSelect value={String(sortCriteria)} onChange={(e) => setSortCriteria(e.target.value as keyof T)} aria-label="Trier par">
              {sortOptions.map((option) => (
                <option key={option.value as string} value={option.value as string}>
                  {option.label}
                </option>
              ))}
            </SortSelect> */}
            <CustomSelect
              options={sortOptions.map((option) => ({ value: option.value as string, label: option.label }))}
              value={sortCriteria as string}
              onChange={(val) => setSortCriteria(val as keyof T)}
            />
          </label>
        </div>
        <SortToggle onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}>{sortOrder === "asc" ? "Asc" : "Desc"}</SortToggle>
      </SortControls>
      {sortedItems.map((item, index) => (
        <React.Fragment key={index}>{renderItem(item)}</React.Fragment>
      ))}
    </Container>
  )
}

export default FilterAndSortList
